import React from "react";
import { getClassName } from "@utils";
import { useMixpanel } from "@taskpane/context";
import css from "./BottomNav.module.scss";

export interface BottomNavProps<T extends string = string> {
  tabs: Array<{
    id: T;
    label: string;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    showIndicator?: boolean;
  }>;
  activeTabId: T | null;
  onTabClick: (tabId: T) => void;
  disabled?: boolean;
}

const BottomNav = <T extends string>({
  tabs,
  activeTabId,
  disabled = false,
  onTabClick,
}: BottomNavProps<T>): React.JSX.Element => {
  const { trackEvent } = useMixpanel();

  const attrs = {
    container: {
      className: css.container,
    },
    tabsWrapper: {
      className: css.tabsWrapper,
    },
  };

  const renderTabs = () => {
    return tabs.map(({ id, label, icon, showIndicator = false }) => {
      const isActive = activeTabId === id;
      const IconComponent = icon;
      const attrs = {
        tab: {
          disabled,
          type: "button" as const,
          className: getClassName(css.tab, isActive && !disabled && css.tabActive),
          onClick: () => {
            if (!isActive) {
              trackEvent(`bottomNav.${id}TabClick`);
              onTabClick(id);
            }
          },
        },
        iconWrapper: {
          className: getClassName(css.iconWrapper, showIndicator && css.iconWrapperWithIndicator),
        },
        icon: {
          className: css.icon,
        },
        label: {
          className: css.label,
        },
      };

      return (
        <button key={id} {...attrs.tab}>
          <span {...attrs.iconWrapper}>
            <IconComponent {...attrs.icon} />
          </span>
          <span {...attrs.label}>{label}</span>
        </button>
      );
    });
  };

  return (
    <div {...attrs.container}>
      <div {...attrs.tabsWrapper}>{renderTabs()}</div>
    </div>
  );
};

export default BottomNav;
